import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import {
  useDesignerAllowedFromEmails,
  useFileInboxMessage,
  useGamePublishers,
  useMyGames,
  usePublisherGameAsDesigner,
  useUpdateFollowUp,
  useUpdatePublisherGamev2,
} from "@seabrookstudios/pitch2table";
import {
  FollowUpStatusToDaysDefaults,
  formatPublisherStatus,
  PublisherGameStatusLabel,
} from "@seabrookstudios/pitch2table-core";
import { enqueueSnackbar } from "notistack";
import { CoverSquare } from "../games/CoverImages";
import { FilterableGamesList } from "../games/FilterableGamesList";
import { PublisherInterestStatusSelect } from "../games/publisher-interest/PublisherInterestStatusSelect";
import { FullScreenDialog } from "../core/FullScreenDialog";
import { SmartTextField } from "../core/SmartTextField";

/**
 * @param {string} subject
 * @param {string} text
 * @returns {string}
 */
const formatUpdateDraft = (subject, text) => {
  const trimmedSubject = `${subject || ""}`.trim();
  const trimmedText = `${text || ""}`.trim();
  if (!trimmedSubject) {
    return trimmedText;
  }
  if (!trimmedText) {
    return trimmedSubject;
  }
  return `${trimmedSubject}\n\n${trimmedText}`;
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PublisherGameStatus} status
 * @param {number} [existingNextAction]
 * @returns {import("luxon").DateTime|null}
 */
const defaultFollowUpDate = (status, existingNextAction) => {
  if (!FollowUpStatusToDaysDefaults[status]) {
    return null;
  }
  if (existingNextAction && existingNextAction > 0) {
    return DateTime.fromMillis(existingNextAction);
  }
  const days = FollowUpStatusToDaysDefaults[status];
  return DateTime.now().plus({ days });
};

/**
 * Whether this inbox message is the designer's own mail (sent/BCC/forward-of-self).
 * Outer `from` is useless here — forwards to the inbox are always from the designer.
 * Pitch Received keys off a distinct unwrapped `originalFrom` that is not the designer.
 *
 * @param {import("@seabrookstudios/pitch2table-core").InboundEmail} message
 * @param {string[]} designerEmails lowercased allowed designer addresses
 * @returns {boolean}
 */
const isEmailFromDesigner = (message, designerEmails = []) => {
  const originalFrom = `${message.originalFrom || ""}`.trim().toLowerCase();
  if (originalFrom && !designerEmails.includes(originalFrom)) {
    return false;
  }
  return true;
};

/**
 * Default filing status from current submission status + who sent the email.
 *
 * @param {import("@seabrookstudios/pitch2table-core").PublisherGameStatus} currentStatus
 * @param {boolean} fromDesigner
 * @returns {import("@seabrookstudios/pitch2table-core").PublisherGameStatus}
 */
const defaultFilingStatus = (currentStatus, fromDesigner) => {
  if (currentStatus === "ShortListed" && fromDesigner) {
    return "PitchSent";
  }
  if ((currentStatus === "ShortListed" || currentStatus === "PitchSent") && !fromDesigner) {
    return "PitchReceived";
  }
  return currentStatus;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherGameId} props.publisherGameId
 * @param {number} [props.score]
 * @param {import("@seabrookstudios/pitch2table-core").InboxMatchReason[]} [props.reasons]
 * @param {() => void} props.onSelect
 * @param {boolean} [props.disabled]
 */
const CandidateRow = ({ publisherGameId, score, reasons = [], onSelect, disabled }) => {
  const { data: publisherGame, isLoading } = usePublisherGameAsDesigner(publisherGameId);

  if (isLoading) {
    return (
      <ListItemButton disabled>
        <CircularProgress size={20} />
      </ListItemButton>
    );
  }

  if (!publisherGame) {
    return null;
  }

  const reasonText = reasons.length > 0 ? reasons.join(", ") : undefined;
  const secondary = [
    PublisherGameStatusLabel[publisherGame.status] || publisherGame.status,
    score != null ? `score ${score}` : null,
    reasonText,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <ListItemButton onClick={onSelect} disabled={disabled}>
      <ListItemText primary={`${publisherGame.publisherName}`} secondary={secondary} />
    </ListItemButton>
  );
};

/**
 * Review + file step once a publisher submission is chosen.
 * Status and follow-up are applied together with the inbound email file.
 *
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").InboundEmail} props.message
 * @param {import("@seabrookstudios/pitch2table-core").PublisherGameId} props.publisherGameId
 * @param {() => void} props.onChangePublisher
 * @param {() => void} [props.onFiled]
 */
const FileInboxReviewStep = ({ message, publisherGameId, onChangePublisher, onFiled }) => {
  const { data: publisherGame, isLoading } = usePublisherGameAsDesigner(publisherGameId);
  const { data: designerEmails = [], isLoading: designerEmailsLoading } = useDesignerAllowedFromEmails();
  const updatePublisherGame = useUpdatePublisherGamev2(publisherGameId);
  const updateFollowUp = useUpdateFollowUp(publisherGameId);

  const [updateText, setUpdateText] = useState(() => formatUpdateDraft(message.subject, message.text));
  const [status, setStatus] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").PublisherGameStatus|null} */ (null)
  );
  const [followUpDate, setFollowUpDate] = useState(/** @type {import("luxon").DateTime|null} */ (null));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileMessage = useFileInboxMessage(
    () => {},
    () => enqueueSnackbar("Could not file email", { variant: "error" })
  );

  useEffect(() => {
    setUpdateText(formatUpdateDraft(message.subject, message.text));
  }, [message.id, message.subject, message.text]);

  // Seed status + follow-up once the selected submission (and designer emails) load
  useEffect(() => {
    if (!publisherGame || designerEmailsLoading) {
      return;
    }
    const fromDesigner = isEmailFromDesigner(message, designerEmails);
    const nextStatus = defaultFilingStatus(publisherGame.status, fromDesigner);
    setStatus(nextStatus);
    setFollowUpDate(defaultFollowUpDate(nextStatus, publisherGame.nextAction));
    // Only re-seed when switching submissions / message / designer identity resolves
  }, [publisherGame?.id, message.id, designerEmailsLoading, designerEmails.join("|")]);

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PublisherGameStatus} nextStatus
   */
  const onChangeStatus = (nextStatus) => {
    setStatus(nextStatus);
    setFollowUpDate(defaultFollowUpDate(nextStatus));
  };

  const canUpdateFollowUp = status ? !!FollowUpStatusToDaysDefaults[status] : false;
  const busy = isSubmitting || fileMessage.isPending || updatePublisherGame.isPending || updateFollowUp.isPending;

  const onFile = async () => {
    if (!publisherGameId || !updateText.trim() || !status || !publisherGame) {
      return;
    }

    setIsSubmitting(true);
    try {
      await fileMessage.mutateAsync({
        id: message.id,
        request: { publisherGameId, update: updateText.trim() },
      });

      if (status !== publisherGame.status) {
        await updatePublisherGame.mutateAsync({
          status,
          cleanUpOtherPublisherInterest: undefined,
          rules: undefined,
          sellsheet: undefined,
          sentPNP: undefined,
          sizzle: undefined,
          signedDate: undefined,
          externalPortalUrl: undefined,
          nextActionType: undefined,
          expectedReleaseDate: undefined,
          publisherProvidedFeedback: undefined,
        });
      }

      if (canUpdateFollowUp && followUpDate) {
        await updateFollowUp.mutateAsync({ timestamp: followUpDate.toMillis() });
      }

      enqueueSnackbar("Email filed to submission", { variant: "success" });
      onFiled?.();
    } catch {
      // Individual mutations already snackbar on error where wired; keep dialog open
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || designerEmailsLoading || !publisherGame || !status) {
    return <CircularProgress size={24} />;
  }

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Publisher</Typography>
          <Button size="small" onClick={onChangePublisher} disabled={busy}>
            Change publisher
          </Button>
        </Stack>
        <Typography variant="body2">{publisherGame.publisherName}</Typography>
      </Stack>

      <PublisherInterestStatusSelect value={status} onChange={onChangeStatus} disabled={busy} />

      {canUpdateFollowUp ? (
        <DatePicker
          label="Follow-up Date"
          value={followUpDate}
          onChange={setFollowUpDate}
          disabled={busy}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText: "Reminder date for this submission",
            },
          }}
        />
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary">
            No follow up is scheduled for pitches in {formatPublisherStatus(status)} status
          </Typography>
        </Box>
      )}

      <SmartTextField
        key={`${message.id}-${publisherGameId}`}
        label="Publisher update"
        helperText="Edit what will be posted to this submission."
        initialValue={updateText}
        onChange={setUpdateText}
        multiline
        minRows={8}
        onEnterDisabled
        autoFocus
      />

      <Button variant="contained" onClick={onFile} disabled={!updateText.trim() || busy}>
        {busy ? "Filing…" : "Add publisher update"}
      </Button>
    </Stack>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").InboundEmail} props.message
 * @param {() => void} [props.onFiled]
 */
export const FileInboxMessageDialog = ({ message, onFiled }) => {
  const [gameId, setGameId] = useState(/** @type {import("@seabrookstudios/types").GameId|null} */ (null));
  const [publisherGameId, setPublisherGameId] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").PublisherGameId|null} */ (null)
  );
  const [forceClose, setForceClose] = useState(false);

  const { data: games = [], isLoading: gamesLoading } = useMyGames();
  const { data: publishers = [], isLoading: publishersLoading } = useGamePublishers(gameId);
  const { data: selectedPublisherGame } = usePublisherGameAsDesigner(publisherGameId);

  const candidates = useMemo(
    () => [...(message.candidates || [])].sort((a, b) => b.score - a.score),
    [message.candidates]
  );

  /** Sole match — prefill publisher; game resolves via publisherGame lookup. */
  const soleCandidateId = candidates.length === 1 ? candidates[0].publisherGameId : null;

  /** Server sets when exactly one catalogue game name is a subject token. */
  const suggestedGameId = message.suggestedGameId || null;

  const selectedGame = useMemo(() => games.find((game) => game.id === gameId) || null, [games, gameId]);

  // Suggested matches may set publisher before game resolves
  useEffect(() => {
    if (publisherGameId && selectedPublisherGame?.gameId && !gameId) {
      setGameId(selectedPublisherGame.gameId);
    }
  }, [publisherGameId, selectedPublisherGame, gameId]);

  /**
   * @param {import("@seabrookstudios/types").GameId} id
   */
  const onToggleGame = (id) => {
    setGameId((current) => (current === id ? null : id));
    setPublisherGameId(null);
  };

  const onChangeGame = () => {
    setGameId(null);
    setPublisherGameId(null);
  };

  const onChangePublisher = () => {
    setPublisherGameId(null);
  };

  /**
   * Jump straight to review via a suggested match (game + publisher known).
   * @param {import("@seabrookstudios/pitch2table-core").PublisherGameId} id
   * @param {import("@seabrookstudios/types").GameId|undefined} matchedGameId
   */
  const onSelectCandidate = (id, matchedGameId) => {
    if (matchedGameId) {
      setGameId(matchedGameId);
    }
    setPublisherGameId(id);
  };

  const handleFiled = () => {
    setForceClose(true);
    onFiled?.();
  };

  const resetSelection = () => {
    setForceClose(false);
    setGameId(suggestedGameId);
    setPublisherGameId(soleCandidateId);
  };

  return (
    <FullScreenDialog
      id="file-inbox-message"
      title="File email"
      subtitle="Attach this email to a publisher submission"
      canSave={false}
      forceClose={forceClose}
      onOpen={resetSelection}
      CustomControl={
        <Button variant="contained" size="small">
          File to submission
        </Button>
      }
    >
      <Stack spacing={2} p={2}>
        {candidates.length > 0 && !publisherGameId && (
          <Stack spacing={1}>
            <Typography variant="subtitle2">Suggested matches</Typography>
            <List dense>
              {candidates.map((candidate) => (
                <CandidateRowWithGame
                  key={candidate.publisherGameId}
                  publisherGameId={candidate.publisherGameId}
                  score={candidate.score}
                  reasons={candidate.reasons}
                  onSelect={onSelectCandidate}
                />
              ))}
            </List>
          </Stack>
        )}

        {!gameId ? (
          <Stack spacing={1}>
            <Typography variant="subtitle2">Pick a game</Typography>
            {gamesLoading ? (
              <CircularProgress size={24} />
            ) : (
              <FilterableGamesList
                games={games}
                selectedGames={[]}
                onToggleGame={onToggleGame}
                defaultSelectedStatuses={["PitchingNow"]}
              />
            )}
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Game</Typography>
              <Button size="small" onClick={onChangeGame}>
                Change game
              </Button>
            </Stack>
            {selectedGame && (
              <List dense>
                <ListItemButton disabled>
                  <ListItemAvatar>
                    <CoverSquare game={selectedGame} width={48} height={48} />
                  </ListItemAvatar>
                  <ListItemText primary={selectedGame.name} secondary={selectedGame.tag} />
                </ListItemButton>
              </List>
            )}
          </Stack>
        )}

        {gameId && !publisherGameId && (
          <Stack spacing={1}>
            <Typography variant="subtitle2">Pick a publisher submission</Typography>
            {publishersLoading ? (
              <CircularProgress size={24} />
            ) : publishers.length === 0 ? (
              <Alert severity="warning">No publisher submissions for this game.</Alert>
            ) : (
              <List dense>
                {publishers.map((publisherGame) => (
                  <ListItemButton key={publisherGame.id} onClick={() => setPublisherGameId(publisherGame.id)}>
                    <ListItemText
                      primary={publisherGame.publisherName}
                      secondary={PublisherGameStatusLabel[publisherGame.status] || publisherGame.status}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Stack>
        )}

        {publisherGameId && (
          <FileInboxReviewStep
            message={message}
            publisherGameId={publisherGameId}
            onChangePublisher={onChangePublisher}
            onFiled={handleFiled}
          />
        )}
      </Stack>
    </FullScreenDialog>
  );
};

/**
 * Suggested match row that also resolves gameId for jump-to-review.
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherGameId} props.publisherGameId
 * @param {number} [props.score]
 * @param {import("@seabrookstudios/pitch2table-core").InboxMatchReason[]} [props.reasons]
 * @param {(
 *   publisherGameId: import("@seabrookstudios/pitch2table-core").PublisherGameId,
 *   gameId: import("@seabrookstudios/types").GameId|undefined
 * ) => void} props.onSelect
 * @param {boolean} [props.disabled]
 */
const CandidateRowWithGame = ({ publisherGameId, score, reasons, onSelect, disabled }) => {
  const { data: publisherGame } = usePublisherGameAsDesigner(publisherGameId);

  return (
    <CandidateRow
      publisherGameId={publisherGameId}
      score={score}
      reasons={reasons}
      disabled={disabled}
      onSelect={() => onSelect(publisherGameId, publisherGame?.gameId)}
    />
  );
};
